import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db, storage } from '@/lib/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LayoutDashboard, Users, MessageSquare, HelpCircle, Settings, LogOut, Home, Plus, Trash2, Edit, Shield, Image, Facebook, Instagram, Youtube, Upload, FileText, BookOpen, PlayCircle, Link2, Copy, DollarSign, Target, ListMusic, X, ChevronLeft, Calendar, Music2, StickyNote, Award, Download, FileDown } from 'lucide-react';
import jsPDF from 'jspdf';

import { defaultTestimonials, defaultFAQs, defaultResources, defaultPricingServices } from '@/lib/defaults';

function compressImage(file, maxWidth = 800, quality = 0.5) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [students, setStudents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  // Student form
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [newStudentName, setNewStudentName] = useState('');

  // Student detail view for managing homework, notes, videos, repertoire, goals
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentData, setStudentData] = useState({ homework: [], notes: [], videos: [], repertoire: [], goals: [] });
  const [hwForm, setHwForm] = useState({ title: '', due: '' });
  const [hwDialogOpen, setHwDialogOpen] = useState(false);
  const [editingHwId, setEditingHwId] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '', date: new Date().toISOString().split('T')[0] });
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: '', url: '' });
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [repForm, setRepForm] = useState({ title: '', artist: '', lyricsUrl: '' });
  const [repDialogOpen, setRepDialogOpen] = useState(false);
  const [editingRepId, setEditingRepId] = useState(null);
  const [goalForm, setGoalForm] = useState({ title: '', progress: 0 });
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [adminNotesForm, setAdminNotesForm] = useState('');

  // Certificates & Song Requests
  const [certificates, setCertificates] = useState([]);
  const [songRequests, setSongRequests] = useState([]);
  const [certUploading, setCertUploading] = useState(false);
  const [certForm, setCertForm] = useState({ title: '', date: new Date().toISOString().split('T')[0] });
  const certInputRef = useRef(null);

  // Testimonial form
  const [testForm, setTestForm] = useState({ name: '', role: '', text: '', rating: 5 });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingTestId, setEditingTestId] = useState(null);

  // FAQ form
  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState(null);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    tasterEnabled: true, tasterDate: '', tasterTime: '', tasterLocation: 'Bristol Cathedral Choir School',
    bookLessonsEnabled: true, waitlistMode: false, adminEmail: 'catherine@singvolve.com',
    lessonPrice: '25', lessonCount: '6', lessonDuration: '30',
    socialLinks: { facebook: '', instagram: '', youtube: '' },
    images: { hero: '', about: '', location: '' },
  });

  // Resources
  const [resources, setResources] = useState([]);
  const [resForm, setResForm] = useState({ title: '', description: '', type: 'article', url: '' });
  const [resDialogOpen, setResDialogOpen] = useState(false);
  const [editingResId, setEditingResId] = useState(null);

  // Pricing Services
  const [pricingServices, setPricingServices] = useState([]);
  const [pricingForm, setPricingForm] = useState({
    name: '',
    description: '',
    price: '',
    priceLabel: 'per lesson',
    lessonCount: '',
    lessonDuration: '',
    features: [],
    featuresText: '',
    ctaText: 'Book Now',
    ctaType: 'book',
    badge: '',
    highlighted: false,
    enabled: true,
  });
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricingId, setEditingPricingId] = useState(null);

  // About page content
  const [aboutForm, setAboutForm] = useState({
    heading: 'Meet Your Singing Teacher',
    body: "Welcome to Singvolve! I'm Catherine, a passionate and experienced singing teacher based in Bristol.\nI believe every voice has a unique story to tell, and my mission is to help you discover and develop yours.\n\nWith years of experience teaching children and adults of all abilities, I create a warm, encouraging\nenvironment where students feel safe to explore their voice, take risks, and grow in confidence.\n\nWhether you're a complete beginner dreaming of singing your favourite songs, a parent looking for\nquality vocal tuition for your child, or an experienced singer wanting to refine your technique,\nI'm here to guide you every step of the way.\n\nLessons take place at the beautiful Bristol Cathedral Choir School, providing an inspiring setting\nfor musical development. I offer one-to-one lessons tailored entirely to your goals, interests, and pace.",
  });

  // Lessons page content
  const [lessonsForm, setLessonsForm] = useState({
    heading: 'Singing Lessons for Everyone',
    intro: 'Every lesson is a one-to-one experience, tailored to your goals and pace. Whether you\'re 7 or 70, a beginner or experienced, there\'s a place for you at Singvolve.',
    cards: [
      { title: 'Children & Young People', desc: 'Fun, engaging lessons that build confidence and a love of singing. We work on breathing, pitch, rhythm, and performing favourite songs in a supportive environment.', tags: 'Ages 7+, Build confidence, All abilities' },
      { title: 'Adult Beginners', desc: "It's never too late to start singing! Whether you've always wanted to sing or are returning after a break, lessons are designed to make you feel at ease from the very first session.", tags: 'No experience needed, Relaxed pace, Encouraging' },
      { title: 'Developing Singers', desc: 'For students with some experience who want to refine their technique, expand their range, or prepare for performances, auditions, or exams.', tags: 'Technique building, Performance prep, Repertoire expansion' },
      { title: 'What You Will Learn', desc: 'Lessons cover breathing technique, vocal warm-ups, pitch accuracy, tone development, rhythm, stage presence, and working on songs across a wide range of genres.', tags: 'Pop, Musical Theatre, Classical, Folk' },
    ],
  });

  // Invitation link
  const portalUrl = window.location.origin + '/portal-login';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const studentsSnap = await getDocs(collection(db, 'students'));
      setStudents(studentsSnap.docs.map(d => ({ ...d.data(), id: d.id })));

      // Load testimonials — seed defaults if empty
      const testSnap = await getDocs(collection(db, 'testimonials'));
      if (testSnap.empty) {
        for (const { id: _id, ...t } of defaultTestimonials) {
          await addDoc(collection(db, 'testimonials'), { ...t, createdAt: new Date().toISOString() });
        }
        const reloadTest = await getDocs(collection(db, 'testimonials'));
        setTestimonials(reloadTest.docs.map(d => ({ ...d.data(), id: d.id })));
      } else {
        setTestimonials(testSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      }

      // Load FAQs — seed defaults if empty
      const faqSnap = await getDocs(collection(db, 'faqs'));
      if (faqSnap.empty) {
        for (const { id: _id, ...f } of defaultFAQs) {
          await addDoc(collection(db, 'faqs'), { ...f, createdAt: new Date().toISOString() });
        }
        const reloadFaq = await getDocs(collection(db, 'faqs'));
        setFaqs(reloadFaq.docs.map(d => ({ ...d.data(), id: d.id })));
      } else {
        setFaqs(faqSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      }

      const settingsSnap = await getDoc(doc(db, 'siteSettings', 'main'));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        setSettingsForm(prev => ({
          ...prev,
          ...data,
          socialLinks: { ...prev.socialLinks, ...(data.socialLinks || {}) },
          images: { ...prev.images, ...(data.images || {}) },
        }));
      }

      // Load resources — seed defaults if empty
      const resSnap = await getDocs(collection(db, 'resources'));
      if (resSnap.empty) {
        for (const { id: _id, ...r } of defaultResources) {
          await addDoc(collection(db, 'resources'), { ...r, createdAt: new Date().toISOString() });
        }
        const reloadRes = await getDocs(collection(db, 'resources'));
        setResources(reloadRes.docs.map(d => ({ ...d.data(), id: d.id })));
      } else {
        setResources(resSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      }

      // Load pricing services — seed defaults if empty
      const pricingSnap = await getDocs(collection(db, 'pricingServices'));
      if (pricingSnap.empty) {
        for (const { id: _id, ...p } of defaultPricingServices) {
          await addDoc(collection(db, 'pricingServices'), { ...p, createdAt: new Date().toISOString() });
        }
        const reloadPricing = await getDocs(collection(db, 'pricingServices'));
        setPricingServices(reloadPricing.docs.map(d => ({ ...d.data(), id: d.id })));
      } else {
        setPricingServices(pricingSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      }

      // Load about page content
      const aboutSnap = await getDoc(doc(db, 'pages', 'about'));
      if (aboutSnap.exists()) setAboutForm(prev => ({ ...prev, ...aboutSnap.data() }));

      // Load lessons page content
      const lessonsSnap = await getDoc(doc(db, 'pages', 'lessons'));
      if (lessonsSnap.exists()) setLessonsForm(prev => ({ ...prev, ...lessonsSnap.data() }));
    } catch (e) {
      console.error('Load error:', e);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Student CRUD
  const addStudent = async (e) => {
    e.preventDefault();
    if (!newStudentEmail.trim()) return;
    setLoading(true);
    try {
      await addDoc(collection(db, 'students'), {
        email: newStudentEmail.toLowerCase().trim(),
        name: newStudentName.trim(),
        enabled: true,
        gamificationEnabled: true,
        createdAt: new Date().toISOString(),
      });
      setNewStudentEmail('');
      setNewStudentName('');
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggleStudent = async (id, enabled) => {
    await updateDoc(doc(db, 'students', id), { enabled: !enabled });
    await loadData();
  };

  const removeStudent = async (id) => {
    if (!window.confirm('Remove this student?')) return;
    await deleteDoc(doc(db, 'students', id));
    await loadData();
  };

  // Load student-specific data (homework, notes, videos, repertoire, goals)
  const loadStudentData = async (studentId) => {
    try {
      const hwSnap = await getDocs(collection(db, 'students', studentId, 'homework'));
      const notesSnap = await getDocs(collection(db, 'students', studentId, 'notes'));
      const videosSnap = await getDocs(collection(db, 'students', studentId, 'videos'));
      const repSnap = await getDocs(collection(db, 'students', studentId, 'repertoire'));
      const goalsSnap = await getDocs(collection(db, 'students', studentId, 'goals'));
      const certsSnap = await getDocs(collection(db, 'students', studentId, 'certificates'));
      const songsSnap = await getDocs(collection(db, 'students', studentId, 'songRequests'));
      
      setStudentData({
        homework: hwSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        notes: notesSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        videos: videosSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        repertoire: repSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        goals: goalsSnap.docs.map(d => ({ ...d.data(), id: d.id })),
      });
      setCertificates(certsSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      setSongRequests(songsSnap.docs.map(d => ({ ...d.data(), id: d.id })));
    } catch (e) {
      console.error('Error loading student data:', e);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setAdminNotesForm(student.adminNotes || '');
    await loadStudentData(student.id);
  };

  const saveAdminNotes = async () => {
    if (!selectedStudent) return;
    await updateDoc(doc(db, 'students', selectedStudent.id), { adminNotes: adminNotesForm });
    setSelectedStudent(prev => ({ ...prev, adminNotes: adminNotesForm }));
    alert('Notes saved!');
  };

  // Homework CRUD for selected student
  const saveHomework = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setLoading(true);
    try {
      if (editingHwId) {
        await updateDoc(doc(db, 'students', selectedStudent.id, 'homework', editingHwId), { ...hwForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'students', selectedStudent.id, 'homework'), { ...hwForm, completed: false, createdAt: new Date().toISOString() });
      }
      setHwForm({ title: '', due: '' });
      setEditingHwId(null);
      setHwDialogOpen(false);
      await loadStudentData(selectedStudent.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editHomework = (hw) => {
    setHwForm({ title: hw.title || '', due: hw.due || '' });
    setEditingHwId(hw.id);
    setHwDialogOpen(true);
  };

  const removeHomework = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this homework?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'homework', id));
    await loadStudentData(selectedStudent.id);
  };

  // Lesson Notes CRUD for selected student
  const saveNote = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setLoading(true);
    try {
      if (editingNoteId) {
        await updateDoc(doc(db, 'students', selectedStudent.id, 'notes', editingNoteId), { ...noteForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'students', selectedStudent.id, 'notes'), { ...noteForm, createdAt: new Date().toISOString() });
      }
      setNoteForm({ title: '', content: '', date: new Date().toISOString().split('T')[0] });
      setEditingNoteId(null);
      setNoteDialogOpen(false);
      await loadStudentData(selectedStudent.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editNote = (note) => {
    setNoteForm({ title: note.title || '', content: note.content || '', date: note.date || '' });
    setEditingNoteId(note.id);
    setNoteDialogOpen(true);
  };

  const removeNote = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this note?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'notes', id));
    await loadStudentData(selectedStudent.id);
  };

  // Videos CRUD for selected student
  const saveVideo = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setLoading(true);
    try {
      if (editingVideoId) {
        await updateDoc(doc(db, 'students', selectedStudent.id, 'videos', editingVideoId), { ...videoForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'students', selectedStudent.id, 'videos'), { ...videoForm, createdAt: new Date().toISOString() });
      }
      setVideoForm({ title: '', url: '' });
      setEditingVideoId(null);
      setVideoDialogOpen(false);
      await loadStudentData(selectedStudent.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editVideo = (video) => {
    setVideoForm({ title: video.title || '', url: video.url || '' });
    setEditingVideoId(video.id);
    setVideoDialogOpen(true);
  };

  const removeVideo = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this video?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'videos', id));
    await loadStudentData(selectedStudent.id);
  };

  // Repertoire CRUD for selected student
  const saveRepertoire = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setLoading(true);
    try {
      if (editingRepId) {
        await updateDoc(doc(db, 'students', selectedStudent.id, 'repertoire', editingRepId), { ...repForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'students', selectedStudent.id, 'repertoire'), { ...repForm, createdAt: new Date().toISOString() });
      }
      setRepForm({ title: '', artist: '', lyricsUrl: '' });
      setEditingRepId(null);
      setRepDialogOpen(false);
      await loadStudentData(selectedStudent.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editRepertoire = (rep) => {
    setRepForm({ title: rep.title || '', artist: rep.artist || '', lyricsUrl: rep.lyricsUrl || '' });
    setEditingRepId(rep.id);
    setRepDialogOpen(true);
  };

  const removeRepertoire = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this song?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'repertoire', id));
    await loadStudentData(selectedStudent.id);
  };

  // Goals CRUD for selected student
  const saveGoal = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setLoading(true);
    try {
      if (editingGoalId) {
        await updateDoc(doc(db, 'students', selectedStudent.id, 'goals', editingGoalId), { ...goalForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'students', selectedStudent.id, 'goals'), { ...goalForm, createdAt: new Date().toISOString() });
      }
      setGoalForm({ title: '', progress: 0 });
      setEditingGoalId(null);
      setGoalDialogOpen(false);
      await loadStudentData(selectedStudent.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editGoal = (goal) => {
    setGoalForm({ title: goal.title || '', progress: goal.progress || 0 });
    setEditingGoalId(goal.id);
    setGoalDialogOpen(true);
  };

  const removeGoal = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this goal?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'goals', id));
    await loadStudentData(selectedStudent.id);
  };

  // Certificate Upload
  const uploadCertificate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStudent) return;
    setCertUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `certificates/${selectedStudent.id}/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(db, 'students', selectedStudent.id, 'certificates'), {
        title: certForm.title || file.name,
        date: certForm.date,
        fileName,
        url,
        createdAt: new Date().toISOString(),
      });
      setCertForm({ title: '', date: new Date().toISOString().split('T')[0] });
      if (certInputRef.current) certInputRef.current.value = '';
      await loadStudentData(selectedStudent.id);
    } catch (err) {
      console.error('Certificate upload error:', err);
      alert('Failed to upload certificate');
    }
    setCertUploading(false);
  };

  const removeCertificate = async (cert) => {
    if (!selectedStudent || !window.confirm('Delete this certificate?')) return;
    try {
      await deleteObject(ref(storage, `certificates/${selectedStudent.id}/${cert.fileName}`));
      await deleteDoc(doc(db, 'students', selectedStudent.id, 'certificates', cert.id));
      await loadStudentData(selectedStudent.id);
    } catch (err) {
      console.error('Error deleting certificate:', err);
      alert('Failed to delete certificate');
    }
  };

  // Song Request Management (delete only - students create)
  const removeSongRequest = async (id) => {
    if (!selectedStudent || !window.confirm('Delete this song request?')) return;
    await deleteDoc(doc(db, 'students', selectedStudent.id, 'songRequests', id));
    await loadStudentData(selectedStudent.id);
  };

  // Generate Progress Report PDF
  const generateProgressReport = () => {
    if (!selectedStudent) return;
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;
    
    // Header
    pdf.setFontSize(20);
    pdf.setTextColor(75, 29, 94);
    pdf.text('Singvolve Progress Report', pageWidth / 2, y, { align: 'center' });
    y += 15;
    
    // Student info
    pdf.setFontSize(14);
    pdf.setTextColor(45, 27, 54);
    pdf.text(`Student: ${selectedStudent.name || selectedStudent.email}`, 20, y);
    y += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Report Generated: ${new Date().toLocaleDateString()}`, 20, y);
    y += 15;
    
    // Goals Section
    pdf.setFontSize(14);
    pdf.setTextColor(75, 29, 94);
    pdf.text('Goals Progress', 20, y);
    y += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(45, 27, 54);
    if (studentData.goals.length > 0) {
      studentData.goals.forEach(goal => {
        pdf.text(`• ${goal.title}: ${goal.progress || 0}%`, 25, y);
        y += 6;
      });
    } else {
      pdf.text('No goals set yet.', 25, y);
      y += 6;
    }
    y += 10;
    
    // Recent Lesson Notes
    pdf.setFontSize(14);
    pdf.setTextColor(75, 29, 94);
    pdf.text('Recent Lesson Notes', 20, y);
    y += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(45, 27, 54);
    const recentNotes = studentData.notes.sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 5);
    if (recentNotes.length > 0) {
      recentNotes.forEach(note => {
        pdf.text(`${note.date || 'N/A'}: ${note.title}`, 25, y);
        y += 6;
        if (note.content) {
          const lines = pdf.splitTextToSize(note.content, pageWidth - 50);
          lines.slice(0, 2).forEach(line => {
            pdf.text(line, 30, y);
            y += 5;
          });
        }
        y += 3;
      });
    } else {
      pdf.text('No lesson notes yet.', 25, y);
      y += 6;
    }
    y += 10;
    
    // Homework Summary
    pdf.setFontSize(14);
    pdf.setTextColor(75, 29, 94);
    pdf.text('Homework Summary', 20, y);
    y += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(45, 27, 54);
    const completedHw = studentData.homework.filter(hw => hw.completed).length;
    const totalHw = studentData.homework.length;
    pdf.text(`Completed: ${completedHw} / ${totalHw} assignments`, 25, y);
    y += 10;
    
    // Repertoire
    pdf.setFontSize(14);
    pdf.setTextColor(75, 29, 94);
    pdf.text('Current Repertoire', 20, y);
    y += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(45, 27, 54);
    if (studentData.repertoire.length > 0) {
      studentData.repertoire.forEach(song => {
        pdf.text(`• ${song.title}${song.artist ? ` - ${song.artist}` : ''}`, 25, y);
        y += 6;
      });
    } else {
      pdf.text('No songs in repertoire yet.', 25, y);
    }
    
    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Singvolve - www.singvolve.com', pageWidth / 2, 285, { align: 'center' });
    
    // Save
    pdf.save(`${(selectedStudent.name || selectedStudent.email).replace(/[^a-z0-9]/gi, '_')}_progress_report.pdf`);
  };

  // Testimonial CRUD
  const saveTestimonial = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingTestId) {
        await updateDoc(doc(db, 'testimonials', editingTestId), { ...testForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'testimonials'), { ...testForm, enabled: true, createdAt: new Date().toISOString() });
      }
      setTestForm({ name: '', role: '', text: '', rating: 5 });
      setEditingTestId(null);
      setTestDialogOpen(false);
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editTestimonial = (t) => {
    setTestForm({ name: t.name || '', role: t.role || '', text: t.text || '', rating: t.rating || 5 });
    setEditingTestId(t.id);
    setTestDialogOpen(true);
  };

  const removeTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await deleteDoc(doc(db, 'testimonials', id));
    await loadData();
  };

  // FAQ CRUD
  const saveFaq = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingFaqId) {
        await updateDoc(doc(db, 'faqs', editingFaqId), { ...faqForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'faqs'), { ...faqForm, order: faqs.length + 1, createdAt: new Date().toISOString() });
      }
      setFaqForm({ question: '', answer: '' });
      setEditingFaqId(null);
      setFaqDialogOpen(false);
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editFaq = (f) => {
    setFaqForm({ question: f.question || '', answer: f.answer || '' });
    setEditingFaqId(f.id);
    setFaqDialogOpen(true);
  };

  const removeFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    await deleteDoc(doc(db, 'faqs', id));
    await loadData();
  };

  // Resource CRUD
  const saveResource = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingResId) {
        await updateDoc(doc(db, 'resources', editingResId), { ...resForm, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'resources'), { ...resForm, order: resources.length + 1, createdAt: new Date().toISOString() });
      }
      setResForm({ title: '', description: '', type: 'article', url: '' });
      setEditingResId(null);
      setResDialogOpen(false);
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const editResource = (r) => {
    setResForm({ title: r.title || '', description: r.description || '', type: r.type || 'article', url: r.url || '' });
    setEditingResId(r.id);
    setResDialogOpen(true);
  };

  const removeResource = async (id) => {
    console.log('removeResource called with id:', id);
    const confirmed = window.confirm('Delete this resource?');
    console.log('User confirmed:', confirmed);
    if (!confirmed) return;
    try {
      console.log('Attempting to delete from Firestore...');
      await deleteDoc(doc(db, 'resources', id));
      console.log('Delete successful, reloading data...');
      await loadData();
      console.log('Data reloaded');
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  // Pricing Services CRUD
  const savePricingService = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Convert features text to array
      const featuresArray = pricingForm.featuresText
        ? pricingForm.featuresText.split('\n').map(f => f.trim()).filter(f => f)
        : pricingForm.features || [];

      const dataToSave = {
        name: pricingForm.name,
        description: pricingForm.description,
        price: pricingForm.price,
        priceLabel: pricingForm.priceLabel,
        lessonCount: pricingForm.lessonCount,
        lessonDuration: pricingForm.lessonDuration,
        features: featuresArray,
        ctaText: pricingForm.ctaText,
        ctaType: pricingForm.ctaType,
        badge: pricingForm.badge,
        highlighted: pricingForm.highlighted,
        enabled: pricingForm.enabled,
      };

      if (editingPricingId) {
        await updateDoc(doc(db, 'pricingServices', editingPricingId), { ...dataToSave, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, 'pricingServices'), { ...dataToSave, order: pricingServices.length + 1, createdAt: new Date().toISOString() });
      }
      resetPricingForm();
      setPricingDialogOpen(false);
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const resetPricingForm = () => {
    setPricingForm({
      name: '',
      description: '',
      price: '',
      priceLabel: 'per lesson',
      lessonCount: '',
      lessonDuration: '',
      features: [],
      featuresText: '',
      ctaText: 'Book Now',
      ctaType: 'book',
      badge: '',
      highlighted: false,
      enabled: true,
    });
    setEditingPricingId(null);
  };

  const editPricingService = (p) => {
    setPricingForm({
      name: p.name || '',
      description: p.description || '',
      price: p.price || '',
      priceLabel: p.priceLabel || 'per lesson',
      lessonCount: p.lessonCount || '',
      lessonDuration: p.lessonDuration || '',
      features: p.features || [],
      featuresText: (p.features || []).join('\n'),
      ctaText: p.ctaText || 'Book Now',
      ctaType: p.ctaType || 'book',
      badge: p.badge || '',
      highlighted: p.highlighted || false,
      enabled: p.enabled !== false,
    });
    setEditingPricingId(p.id);
    setPricingDialogOpen(true);
  };

  const removePricingService = async (id) => {
    console.log('removePricingService called with id:', id);
    const confirmed = window.confirm('Delete this service/package?');
    console.log('User confirmed:', confirmed);
    if (!confirmed) return;
    try {
      console.log('Attempting to delete from Firestore...');
      await deleteDoc(doc(db, 'pricingServices', id));
      console.log('Delete successful, reloading data...');
      await loadData();
      console.log('Data reloaded');
    } catch (error) {
      console.error('Error deleting pricing service:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const togglePricingService = async (id, currentEnabled) => {
    await updateDoc(doc(db, 'pricingServices', id), { enabled: !currentEnabled });
    await loadData();
  };

  // About page save
  const saveAbout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setDoc(doc(db, 'pages', 'about'), { ...aboutForm, updatedAt: new Date().toISOString() });
      alert('About page saved!');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Lessons page save
  const saveLessons = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setDoc(doc(db, 'pages', 'lessons'), { ...lessonsForm, updatedAt: new Date().toISOString() });
      alert('Lessons page saved!');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateLessonCard = (index, field, value) => {
    setLessonsForm(prev => ({
      ...prev,
      cards: prev.cards.map((c, i) => i === index ? { ...c, [field]: value } : c),
    }));
  };

  const addLessonCard = () => {
    setLessonsForm(prev => ({
      ...prev,
      cards: [...prev.cards, { title: '', desc: '', tags: '' }],
    }));
  };

  const removeLessonCard = (index) => {
    setLessonsForm(prev => ({
      ...prev,
      cards: prev.cards.filter((_, i) => i !== index),
    }));
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(portalUrl);
    alert('Invitation link copied!');
  };

  // Settings
  const saveSettings = async (e) => {
    e.preventDefault();
    setSaveStatus(null);
    setLoading(true);
    try {
      await setDoc(doc(db, 'siteSettings', 'main'), { ...settingsForm, updatedAt: new Date().toISOString() });
      setSaveStatus({ type: 'success', message: 'Settings saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error(err);
      setSaveStatus({ type: 'error', message: 'Failed to save settings: ' + err.message });
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/"><img src="/logo.jpg" alt="Singvolve" className="h-8 w-auto" /></Link>
            <Badge className="bg-[#4B1D5E] text-white text-xs">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5E4B66] hidden sm:block">{user?.email}</span>
            <Link to="/"><Button variant="ghost" size="icon" className="text-[#5E4B66]" data-testid="admin-home-btn"><Home className="h-4 w-4" /></Button></Link>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#5E4B66]" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white border border-purple-100 rounded-xl p-1 flex flex-wrap gap-1 h-auto" data-testid="admin-tabs">
            {[
              { value: 'overview', icon: LayoutDashboard, label: 'Overview' },
              { value: 'students', icon: Users, label: 'Students' },
              { value: 'testimonials', icon: MessageSquare, label: 'Testimonials' },
              { value: 'faqs', icon: HelpCircle, label: 'FAQs' },
              { value: 'resources', icon: PlayCircle, label: 'Resources' },
              { value: 'pricing', icon: DollarSign, label: 'Pricing' },
              { value: 'pages', icon: FileText, label: 'Pages' },
              { value: 'settings', icon: Settings, label: 'Settings' },
            ].map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#4B1D5E] data-[state=active]:text-white" data-testid={`admin-tab-${tab.value}`}>
                <tab.icon className="h-4 w-4 mr-2" />{tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Students', count: students.length, icon: Users, color: '#4B1D5E' },
                { label: 'Active', count: students.filter(s => s.enabled).length, icon: Shield, color: '#B088BC' },
                { label: 'Testimonials', count: testimonials.length, icon: MessageSquare, color: '#4B1D5E' },
                { label: 'FAQs', count: faqs.length, icon: HelpCircle, color: '#B088BC' },
              ].map((stat) => (
                <Card key={stat.label} className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center">
                      <stat.icon className="h-6 w-6" style={{ color: stat.color }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#2D1B36]">{stat.count}</p>
                      <p className="text-xs text-[#8A7E90]">{stat.label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Students */}
          <TabsContent value="students">
            {selectedStudent ? (
              /* Student Detail View */
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Button type="button" variant="ghost" onClick={() => setSelectedStudent(null)} className="text-[#4B1D5E]" data-testid="admin-student-back">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back to Students
                  </Button>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-[#2D1B36]">{selectedStudent.name || selectedStudent.email}</h2>
                    <p className="text-sm text-[#8A7E90]">{selectedStudent.email}</p>
                  </div>
                </div>

                {/* Admin Notes */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <StickyNote className="h-5 w-5 text-[#4B1D5E]" />
                      <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Admin Notes</h3>
                    </div>
                    <Textarea value={adminNotesForm} onChange={(e) => setAdminNotesForm(e.target.value)} rows={3} placeholder="Private notes about this student (only visible to admin)..." className="mb-3" data-testid="admin-student-notes" />
                    <Button type="button" onClick={saveAdminNotes} className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-save-notes-btn">Save Notes</Button>
                  </CardContent>
                </Card>

                {/* Homework */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Homework</h3>
                      </div>
                      <Dialog open={hwDialogOpen} onOpenChange={(open) => { setHwDialogOpen(open); if (!open) { setEditingHwId(null); setHwForm({ title: '', due: '' }); } }}>
                        <DialogTrigger asChild>
                          <Button type="button" size="sm" className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-hw-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white rounded-2xl">
                          <DialogHeader><DialogTitle className="font-heading">{editingHwId ? 'Edit Homework' : 'Add Homework'}</DialogTitle></DialogHeader>
                          <form onSubmit={saveHomework} className="space-y-4">
                            <div><Label>Task</Label><Input value={hwForm.title} onChange={(e) => setHwForm({ ...hwForm, title: e.target.value })} required placeholder="e.g. Practice breathing exercises" data-testid="admin-hw-title" /></div>
                            <div><Label>Due Date</Label><Input type="date" value={hwForm.due} onChange={(e) => setHwForm({ ...hwForm, due: e.target.value })} data-testid="admin-hw-due" /></div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg">{editingHwId ? 'Update' : 'Save'}</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-2">
                      {studentData.homework.map((hw) => (
                        <div key={hw.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div>
                            <p className={`text-sm font-medium ${hw.completed ? 'line-through text-[#8A7E90]' : 'text-[#2D1B36]'}`}>{hw.title}</p>
                            {hw.due && <p className="text-xs text-[#8A7E90]">Due: {hw.due}</p>}
                          </div>
                          <div className="flex items-center gap-1">
                            {hw.completed && <Badge className="bg-green-100 text-green-700 text-xs">Done</Badge>}
                            <Button type="button" variant="ghost" size="icon" onClick={() => editHomework(hw)} className="text-[#4B1D5E]"><Edit className="h-4 w-4" /></Button>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeHomework(hw.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {studentData.homework.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No homework assigned yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Lesson Notes */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Lesson Notes</h3>
                      </div>
                      <Dialog open={noteDialogOpen} onOpenChange={(open) => { setNoteDialogOpen(open); if (!open) { setEditingNoteId(null); setNoteForm({ title: '', content: '', date: new Date().toISOString().split('T')[0] }); } }}>
                        <DialogTrigger asChild>
                          <Button type="button" size="sm" className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-note-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white rounded-2xl">
                          <DialogHeader><DialogTitle className="font-heading">{editingNoteId ? 'Edit Lesson Note' : 'Add Lesson Note'}</DialogTitle></DialogHeader>
                          <form onSubmit={saveNote} className="space-y-4">
                            <div><Label>Lesson Date</Label><Input type="date" value={noteForm.date} onChange={(e) => setNoteForm({ ...noteForm, date: e.target.value })} required data-testid="admin-note-date" /></div>
                            <div><Label>Title</Label><Input value={noteForm.title} onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })} required placeholder="e.g. Breathing technique review" data-testid="admin-note-title" /></div>
                            <div><Label>Notes</Label><Textarea value={noteForm.content} onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })} rows={4} placeholder="What was covered in the lesson..." data-testid="admin-note-content" /></div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg">{editingNoteId ? 'Update' : 'Save'}</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-2">
                      {studentData.notes.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((note) => (
                        <div key={note.id} className="p-3 bg-[#FAFAFA] rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Calendar className="h-3 w-3 text-[#8A7E90]" />
                                <span className="text-xs text-[#8A7E90]">{note.date}</span>
                              </div>
                              <p className="text-sm font-medium text-[#2D1B36]">{note.title}</p>
                              {note.content && <p className="text-sm text-[#5E4B66] mt-1">{note.content}</p>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="ghost" size="icon" onClick={() => editNote(note)} className="text-[#4B1D5E]"><Edit className="h-4 w-4" /></Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeNote(note.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {studentData.notes.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No lesson notes yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Videos */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <PlayCircle className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Videos</h3>
                      </div>
                      <Dialog open={videoDialogOpen} onOpenChange={(open) => { setVideoDialogOpen(open); if (!open) { setEditingVideoId(null); setVideoForm({ title: '', url: '' }); } }}>
                        <DialogTrigger asChild>
                          <Button type="button" size="sm" className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-video-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white rounded-2xl">
                          <DialogHeader><DialogTitle className="font-heading">{editingVideoId ? 'Edit Video' : 'Add Video'}</DialogTitle></DialogHeader>
                          <form onSubmit={saveVideo} className="space-y-4">
                            <div><Label>Title</Label><Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} required placeholder="e.g. Breathing tutorial" data-testid="admin-video-title" /></div>
                            <div><Label>YouTube URL</Label><Input value={videoForm.url} onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })} required placeholder="https://www.youtube.com/watch?v=..." data-testid="admin-video-url" /></div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg">{editingVideoId ? 'Update' : 'Save'}</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-2">
                      {studentData.videos.map((video) => (
                        <div key={video.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-[#2D1B36]">{video.title}</p>
                            <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4B1D5E] hover:underline">{video.url}</a>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="ghost" size="icon" onClick={() => editVideo(video)} className="text-[#4B1D5E]"><Edit className="h-4 w-4" /></Button>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeVideo(video.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {studentData.videos.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No videos added yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Repertoire */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <ListMusic className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Repertoire</h3>
                      </div>
                      <Dialog open={repDialogOpen} onOpenChange={(open) => { setRepDialogOpen(open); if (!open) { setEditingRepId(null); setRepForm({ title: '', artist: '', lyricsUrl: '' }); } }}>
                        <DialogTrigger asChild>
                          <Button type="button" size="sm" className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-rep-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white rounded-2xl">
                          <DialogHeader><DialogTitle className="font-heading">{editingRepId ? 'Edit Song' : 'Add Song'}</DialogTitle></DialogHeader>
                          <form onSubmit={saveRepertoire} className="space-y-4">
                            <div><Label>Song Title</Label><Input value={repForm.title} onChange={(e) => setRepForm({ ...repForm, title: e.target.value })} required placeholder="e.g. Shallow" data-testid="admin-rep-title" /></div>
                            <div><Label>Artist</Label><Input value={repForm.artist} onChange={(e) => setRepForm({ ...repForm, artist: e.target.value })} placeholder="e.g. Lady Gaga" data-testid="admin-rep-artist" /></div>
                            <div><Label>Lyrics/Resource URL</Label><Input value={repForm.lyricsUrl} onChange={(e) => setRepForm({ ...repForm, lyricsUrl: e.target.value })} placeholder="Google Docs, YouTube, or lyrics URL" data-testid="admin-rep-url" /></div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg">{editingRepId ? 'Update' : 'Save'}</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-2">
                      {studentData.repertoire.map((rep) => (
                        <div key={rep.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-[#2D1B36]">{rep.title}</p>
                            {rep.artist && <p className="text-xs text-[#8A7E90]">{rep.artist}</p>}
                            {rep.lyricsUrl && <a href={rep.lyricsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4B1D5E] hover:underline">View lyrics/resource</a>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="ghost" size="icon" onClick={() => editRepertoire(rep)} className="text-[#4B1D5E]"><Edit className="h-4 w-4" /></Button>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeRepertoire(rep.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {studentData.repertoire.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No songs in repertoire yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Goals */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Goals</h3>
                      </div>
                      <Dialog open={goalDialogOpen} onOpenChange={(open) => { setGoalDialogOpen(open); if (!open) { setEditingGoalId(null); setGoalForm({ title: '', progress: 0 }); } }}>
                        <DialogTrigger asChild>
                          <Button type="button" size="sm" className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-goal-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white rounded-2xl">
                          <DialogHeader><DialogTitle className="font-heading">{editingGoalId ? 'Edit Goal' : 'Add Goal'}</DialogTitle></DialogHeader>
                          <form onSubmit={saveGoal} className="space-y-4">
                            <div><Label>Goal Title</Label><Input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} required placeholder="e.g. Master breathing technique" data-testid="admin-goal-title" /></div>
                            <div>
                              <Label>Progress ({goalForm.progress}%)</Label>
                              <input type="range" min="0" max="100" value={goalForm.progress} onChange={(e) => setGoalForm({ ...goalForm, progress: parseInt(e.target.value) })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#4B1D5E]" data-testid="admin-goal-progress" />
                            </div>
                            <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg">{editingGoalId ? 'Update' : 'Save'}</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-2">
                      {studentData.goals.map((goal) => (
                        <div key={goal.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div className="flex-1 mr-4">
                            <p className="text-sm font-medium text-[#2D1B36] mb-1">{goal.title}</p>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-[#4B1D5E] h-2 rounded-full transition-all" style={{ width: `${goal.progress}%` }}></div>
                            </div>
                            <p className="text-xs text-[#8A7E90] mt-1">{goal.progress}% complete</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="ghost" size="icon" onClick={() => editGoal(goal)} className="text-[#4B1D5E]"><Edit className="h-4 w-4" /></Button>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeGoal(goal.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {studentData.goals.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No goals set yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Certificates */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Certificates</h3>
                      </div>
                    </div>
                    <div className="p-4 bg-[#F5F0F7] rounded-xl mb-4">
                      <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <Input placeholder="Certificate title (e.g. Term 1 Achievement)" value={certForm.title} onChange={(e) => setCertForm({ ...certForm, title: e.target.value })} data-testid="admin-cert-title" />
                        <Input type="date" value={certForm.date} onChange={(e) => setCertForm({ ...certForm, date: e.target.value })} data-testid="admin-cert-date" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input ref={certInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadCertificate} className="hidden" id="cert-upload" />
                        <label htmlFor="cert-upload" className={`inline-flex items-center gap-2 px-4 py-2 bg-[#4B1D5E] text-white rounded-lg text-sm cursor-pointer hover:bg-[#3A164A] ${certUploading ? 'opacity-50' : ''}`}>
                          <Upload className="h-4 w-4" /> {certUploading ? 'Uploading...' : 'Upload Certificate'}
                        </label>
                        <span className="text-xs text-[#8A7E90]">PDF, JPG, or PNG</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {certificates.map((cert) => (
                        <div key={cert.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div className="flex items-center gap-3">
                            <Award className="h-5 w-5 text-[#FFD700]" />
                            <div>
                              <p className="text-sm font-medium text-[#2D1B36]">{cert.title}</p>
                              <p className="text-xs text-[#8A7E90]">{cert.date}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <a href={cert.url} target="_blank" rel="noopener noreferrer">
                              <Button type="button" variant="ghost" size="icon" className="text-[#4B1D5E]"><Download className="h-4 w-4" /></Button>
                            </a>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeCertificate(cert)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {certificates.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No certificates uploaded yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Song Requests */}
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Music2 className="h-5 w-5 text-[#4B1D5E]" />
                        <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Song Requests</h3>
                      </div>
                      <Badge variant="outline" className="text-xs">{songRequests.length} requests</Badge>
                    </div>
                    <div className="space-y-2">
                      {songRequests.map((req) => (
                        <div key={req.id} className="flex items-start justify-between p-3 bg-[#FAFAFA] rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-[#2D1B36]">{req.title}</p>
                            {req.artist && <p className="text-xs text-[#8A7E90]">{req.artist}</p>}
                            {req.youtubeUrl && (
                              <a href={req.youtubeUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4B1D5E] hover:underline flex items-center gap-1 mt-1">
                                <PlayCircle className="h-3 w-3" /> Watch on YouTube
                              </a>
                            )}
                            {req.notes && <p className="text-xs text-[#5E4B66] mt-1">{req.notes}</p>}
                            <p className="text-xs text-[#8A7E90] mt-1">Requested: {req.createdAt?.split('T')[0]}</p>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeSongRequest(req.id)} className="text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      ))}
                      {songRequests.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-4">No song requests from this student yet.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Generate Report Button */}
                <Card className="bg-gradient-to-r from-[#4B1D5E] to-[#6B21A8] border-0 rounded-2xl shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="text-white">
                        <h3 className="font-heading text-lg font-semibold">Progress Report</h3>
                        <p className="text-sm opacity-80">Generate a PDF summary to share with parents or student</p>
                      </div>
                      <Button type="button" onClick={generateProgressReport} className="bg-white text-[#4B1D5E] hover:bg-gray-100 rounded-lg" data-testid="admin-generate-report-btn">
                        <FileDown className="h-4 w-4 mr-2" /> Download PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Students List View */
              <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Manage Students</h2>
                  </div>
                  <div className="mb-6 p-4 bg-[#F5F0F7] rounded-xl flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-[#2D1B36]">Student Invitation Link</p>
                      <p className="text-xs text-[#5E4B66] mt-1">Share this link with students after adding their email below</p>
                      <code className="text-xs text-[#4B1D5E] mt-1 block break-all">{portalUrl}</code>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={copyInviteLink} className="border-[#4B1D5E] text-[#4B1D5E] shrink-0" data-testid="admin-copy-invite-btn">
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                  </div>
                  <form onSubmit={addStudent} className="flex flex-col sm:flex-row gap-3 mb-6 p-4 bg-[#FAFAFA] rounded-xl">
                    <Input placeholder="Student name" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)}
                      className="h-10 rounded-lg" data-testid="admin-student-name-input" />
                    <Input placeholder="Student email" type="email" value={newStudentEmail} onChange={(e) => setNewStudentEmail(e.target.value)}
                      required className="h-10 rounded-lg" data-testid="admin-student-email-input" />
                    <Button type="submit" disabled={loading} className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg shrink-0" data-testid="admin-add-student-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </form>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.map((s) => (
                        <TableRow key={s.id} className="cursor-pointer hover:bg-[#FAFAFA]" onClick={() => selectStudent(s)}>
                          <TableCell className="font-medium text-[#2D1B36]">{s.name || '-'}</TableCell>
                          <TableCell className="text-[#5E4B66]">{s.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Switch checked={s.enabled} onCheckedChange={() => toggleStudent(s.id, s.enabled)} data-testid={`admin-student-toggle-${s.id}`} />
                              <span className="text-xs">{s.enabled ? 'Active' : 'Disabled'}</span>
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="ghost" size="sm" onClick={() => selectStudent(s)} className="text-[#4B1D5E]" data-testid={`admin-student-manage-${s.id}`}>
                                <Edit className="h-4 w-4 mr-1" /> Manage
                              </Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeStudent(s.id)} className="text-red-500 hover:text-red-700" data-testid={`admin-student-remove-${s.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {students.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="text-center text-[#8A7E90] py-8">No students added yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Testimonials */}
          <TabsContent value="testimonials">
            <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Manage Testimonials</h2>
                  <Dialog open={testDialogOpen} onOpenChange={(open) => { setTestDialogOpen(open); if (!open) { setEditingTestId(null); setTestForm({ name: '', role: '', text: '', rating: 5 }); } }}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-testimonial-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white rounded-2xl">
                      <DialogHeader><DialogTitle className="font-heading">{editingTestId ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle></DialogHeader>
                      <form onSubmit={saveTestimonial} className="space-y-4">
                        <div><Label>Name</Label><Input value={testForm.name} onChange={(e) => setTestForm({ ...testForm, name: e.target.value })} required data-testid="admin-testimonial-name" /></div>
                        <div><Label>Role</Label><Input value={testForm.role} onChange={(e) => setTestForm({ ...testForm, role: e.target.value })} placeholder="e.g. Parent, Adult Student" data-testid="admin-testimonial-role" /></div>
                        <div><Label>Testimonial</Label><Textarea value={testForm.text} onChange={(e) => setTestForm({ ...testForm, text: e.target.value })} required rows={4} data-testid="admin-testimonial-text" /></div>
                        <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={testForm.rating} onChange={(e) => setTestForm({ ...testForm, rating: parseInt(e.target.value) })} data-testid="admin-testimonial-rating" /></div>
                        <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-testimonial-submit">{editingTestId ? 'Update' : 'Save'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-3">
                  {testimonials.map((t) => (
                    <div key={t.id} className="flex items-start justify-between p-4 bg-[#FAFAFA] rounded-xl">
                      <div className="flex-1 mr-3">
                        <p className="font-semibold text-sm text-[#2D1B36]">{t.name} <span className="text-[#8A7E90] font-normal">({t.role})</span></p>
                        <p className="text-sm text-[#5E4B66] mt-1">"{t.text}"</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => editTestimonial(t)} className="text-[#4B1D5E] hover:text-[#3A164A]" data-testid={`admin-edit-testimonial-${t.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeTestimonial(t.id)} className="text-red-500 hover:text-red-700" data-testid={`admin-delete-testimonial-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {testimonials.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-8">No testimonials yet.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQs */}
          <TabsContent value="faqs">
            <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Manage FAQs</h2>
                  <Dialog open={faqDialogOpen} onOpenChange={(open) => { setFaqDialogOpen(open); if (!open) { setEditingFaqId(null); setFaqForm({ question: '', answer: '' }); } }}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-faq-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white rounded-2xl">
                      <DialogHeader><DialogTitle className="font-heading">{editingFaqId ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle></DialogHeader>
                      <form onSubmit={saveFaq} className="space-y-4">
                        <div><Label>Question</Label><Input value={faqForm.question} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} required data-testid="admin-faq-question" /></div>
                        <div><Label>Answer</Label><Textarea value={faqForm.answer} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} required rows={4} data-testid="admin-faq-answer" /></div>
                        <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-faq-submit">{editingFaqId ? 'Update' : 'Save'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-3">
                  {faqs.map((f) => (
                    <div key={f.id} className="flex items-start justify-between p-4 bg-[#FAFAFA] rounded-xl">
                      <div className="flex-1 mr-3">
                        <p className="font-semibold text-sm text-[#2D1B36]">{f.question}</p>
                        <p className="text-sm text-[#5E4B66] mt-1">{f.answer}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => editFaq(f)} className="text-[#4B1D5E] hover:text-[#3A164A]" data-testid={`admin-edit-faq-${f.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeFaq(f.id)} className="text-red-500 hover:text-red-700" data-testid={`admin-delete-faq-${f.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {faqs.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-8">No FAQs yet.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Resources */}
          <TabsContent value="resources">
            <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Manage Resources</h2>
                  <Dialog open={resDialogOpen} onOpenChange={(open) => { setResDialogOpen(open); if (!open) { setEditingResId(null); setResForm({ title: '', description: '', type: 'article', url: '' }); } }}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-resource-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white rounded-2xl">
                      <DialogHeader><DialogTitle className="font-heading">{editingResId ? 'Edit Resource' : 'Add Resource'}</DialogTitle></DialogHeader>
                      <form onSubmit={saveResource} className="space-y-4">
                        <div><Label>Title</Label><Input value={resForm.title} onChange={(e) => setResForm({ ...resForm, title: e.target.value })} required data-testid="admin-resource-title" /></div>
                        <div><Label>Description</Label><Textarea value={resForm.description} onChange={(e) => setResForm({ ...resForm, description: e.target.value })} rows={3} data-testid="admin-resource-desc" /></div>
                        <div>
                          <Label>Type</Label>
                          <select value={resForm.type} onChange={(e) => setResForm({ ...resForm, type: e.target.value })} className="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm" data-testid="admin-resource-type">
                            <option value="article">Article</option>
                            <option value="video">YouTube Video</option>
                            <option value="link">External Link</option>
                          </select>
                        </div>
                        <div><Label>{resForm.type === 'video' ? 'YouTube Embed URL' : 'URL (optional)'}</Label><Input value={resForm.url} onChange={(e) => setResForm({ ...resForm, url: e.target.value })} placeholder={resForm.type === 'video' ? 'https://www.youtube.com/embed/...' : 'https://...'} data-testid="admin-resource-url" /></div>
                        <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-resource-submit">{editingResId ? 'Update' : 'Save'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-3">
                  {resources.map((r) => (
                    <div key={r.id} className="flex items-start justify-between p-4 bg-[#FAFAFA] rounded-xl">
                      <div className="flex-1 mr-3">
                        <div className="flex items-center gap-2 mb-1">
                          {r.type === 'video' ? <PlayCircle className="h-4 w-4 text-[#4B1D5E]" /> : r.type === 'link' ? <Link2 className="h-4 w-4 text-[#4B1D5E]" /> : <BookOpen className="h-4 w-4 text-[#4B1D5E]" />}
                          <span className="text-xs font-medium text-[#4B1D5E] uppercase">{r.type}</span>
                        </div>
                        <p className="font-semibold text-sm text-[#2D1B36]">{r.title}</p>
                        {r.description && <p className="text-sm text-[#5E4B66] mt-1">{r.description}</p>}
                        {r.url && <p className="text-xs text-[#8A7E90] mt-1 truncate">{r.url}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); editResource(r); }} className="text-[#4B1D5E]" data-testid={`admin-edit-resource-${r.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeResource(r.id); }} className="text-red-500" data-testid={`admin-delete-resource-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {resources.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-8">No resources added yet. Add videos, articles, or links for your students.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing */}
          <TabsContent value="pricing">
            <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Manage Pricing & Services</h2>
                    <p className="text-sm text-[#8A7E90] mt-1">Add lesson packages, workshops, or other services</p>
                  </div>
                  <Dialog open={pricingDialogOpen} onOpenChange={(open) => { setPricingDialogOpen(open); if (!open) resetPricingForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-add-pricing-btn"><Plus className="h-4 w-4 mr-1" /> Add Service</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-heading">{editingPricingId ? 'Edit Service' : 'Add Service'}</DialogTitle></DialogHeader>
                      <form onSubmit={savePricingService} className="space-y-4">
                        <div><Label>Service Name *</Label><Input value={pricingForm.name} onChange={(e) => setPricingForm({ ...pricingForm, name: e.target.value })} required placeholder="e.g. Lesson Block, Workshop" data-testid="admin-pricing-name" /></div>
                        <div><Label>Description</Label><Input value={pricingForm.description} onChange={(e) => setPricingForm({ ...pricingForm, description: e.target.value })} placeholder="e.g. Our standard lesson package" data-testid="admin-pricing-desc" /></div>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>Price (£ or "Free")</Label><Input value={pricingForm.price} onChange={(e) => setPricingForm({ ...pricingForm, price: e.target.value })} placeholder="e.g. 25, 0, or Free" data-testid="admin-pricing-price" /></div>
                          <div><Label>Price Label</Label><Input value={pricingForm.priceLabel} onChange={(e) => setPricingForm({ ...pricingForm, priceLabel: e.target.value })} placeholder="e.g. per lesson, per session" data-testid="admin-pricing-label" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>Number of Lessons</Label><Input value={pricingForm.lessonCount} onChange={(e) => setPricingForm({ ...pricingForm, lessonCount: e.target.value })} placeholder="e.g. 6" data-testid="admin-pricing-count" /></div>
                          <div><Label>Duration (minutes)</Label><Input value={pricingForm.lessonDuration} onChange={(e) => setPricingForm({ ...pricingForm, lessonDuration: e.target.value })} placeholder="e.g. 30" data-testid="admin-pricing-duration" /></div>
                        </div>
                        <div><Label>Badge (optional)</Label><Input value={pricingForm.badge} onChange={(e) => setPricingForm({ ...pricingForm, badge: e.target.value })} placeholder="e.g. POPULAR, NEW, LIMITED" data-testid="admin-pricing-badge" /></div>
                        <div>
                          <Label>Features (one per line)</Label>
                          <Textarea 
                            value={pricingForm.featuresText} 
                            onChange={(e) => setPricingForm({ ...pricingForm, featuresText: e.target.value })} 
                            rows={4} 
                            placeholder="One-to-one tuition&#10;Tailored to your goals&#10;Access to Student Portal" 
                            data-testid="admin-pricing-features" 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>Button Text</Label><Input value={pricingForm.ctaText} onChange={(e) => setPricingForm({ ...pricingForm, ctaText: e.target.value })} placeholder="e.g. Book Now" data-testid="admin-pricing-cta" /></div>
                          <div>
                            <Label>Button Action</Label>
                            <select value={pricingForm.ctaType} onChange={(e) => setPricingForm({ ...pricingForm, ctaType: e.target.value })} className="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm" data-testid="admin-pricing-cta-type">
                              <option value="book">Booking Enquiry</option>
                              <option value="enquiry">General Enquiry</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 pt-2">
                          <div className="flex items-center gap-2">
                            <Switch checked={pricingForm.highlighted} onCheckedChange={(v) => setPricingForm({ ...pricingForm, highlighted: v })} data-testid="admin-pricing-highlighted" />
                            <Label className="text-sm">Highlighted (primary style)</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch checked={pricingForm.enabled} onCheckedChange={(v) => setPricingForm({ ...pricingForm, enabled: v })} data-testid="admin-pricing-enabled" />
                            <Label className="text-sm">Enabled</Label>
                          </div>
                        </div>
                        <Button type="submit" disabled={loading} className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid="admin-pricing-submit">{editingPricingId ? 'Update' : 'Save'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-3">
                  {pricingServices.sort((a, b) => (a.order || 99) - (b.order || 99)).map((p) => (
                    <div key={p.id} className={`flex items-start justify-between p-4 rounded-xl ${p.enabled ? 'bg-[#FAFAFA]' : 'bg-gray-100 opacity-60'}`}>
                      <div className="flex-1 mr-3">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="h-4 w-4 text-[#4B1D5E]" />
                          <span className="font-semibold text-sm text-[#2D1B36]">{p.name}</span>
                          {p.badge && <span className="text-xs bg-[#FFD700]/20 text-[#4B1D5E] px-2 py-0.5 rounded-full">{p.badge}</span>}
                          {p.highlighted && <span className="text-xs bg-[#4B1D5E] text-white px-2 py-0.5 rounded-full">Primary</span>}
                          {!p.enabled && <span className="text-xs bg-gray-300 text-gray-600 px-2 py-0.5 rounded-full">Disabled</span>}
                        </div>
                        <p className="text-sm text-[#5E4B66]">{p.description}</p>
                        <p className="text-sm text-[#4B1D5E] font-medium mt-1">
                          {p.price === '0' || p.price === 'Free' ? 'Free' : `£${p.price}`}
                          {p.priceLabel && p.price !== '0' && p.price !== 'Free' && ` ${p.priceLabel}`}
                          {p.lessonCount && ` • ${p.lessonCount} lessons`}
                          {p.lessonDuration && ` • ${p.lessonDuration} min`}
                        </p>
                        {p.features && p.features.length > 0 && (
                          <p className="text-xs text-[#8A7E90] mt-1">{p.features.length} features listed</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Switch 
                          checked={p.enabled !== false} 
                          onCheckedChange={() => togglePricingService(p.id, p.enabled !== false)} 
                          data-testid={`admin-pricing-toggle-${p.id}`}
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); editPricingService(p); }} className="text-[#4B1D5E]" data-testid={`admin-edit-pricing-${p.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removePricingService(p.id); }} className="text-red-500" data-testid={`admin-delete-pricing-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {pricingServices.length === 0 && <p className="text-[#8A7E90] text-sm text-center py-8">No services added yet. Add lesson packages, workshops, or other offerings.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pages */}
          <TabsContent value="pages">
            <div className="space-y-8">
              {/* About Page */}
              <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36] mb-6">About Page</h2>
                  <form onSubmit={saveAbout} className="space-y-4 max-w-2xl">
                    <div>
                      <Label>Page Heading</Label>
                      <Input value={aboutForm.heading} onChange={(e) => setAboutForm({ ...aboutForm, heading: e.target.value })} data-testid="admin-about-heading" />
                    </div>
                    <div>
                      <Label>Page Content</Label>
                      <p className="text-xs text-[#8A7E90] mb-1">Use blank lines to separate paragraphs</p>
                      <Textarea value={aboutForm.body} onChange={(e) => setAboutForm({ ...aboutForm, body: e.target.value })} rows={12} className="font-mono text-sm" data-testid="admin-about-body" />
                    </div>
                    <Button type="submit" disabled={loading} className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg px-8" data-testid="admin-about-save">Save About Page</Button>
                  </form>
                </CardContent>
              </Card>

              {/* Lessons Page */}
              <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36] mb-6">Lessons Page</h2>
                  <form onSubmit={saveLessons} className="space-y-6 max-w-2xl">
                    <div>
                      <Label>Page Heading</Label>
                      <Input value={lessonsForm.heading} onChange={(e) => setLessonsForm({ ...lessonsForm, heading: e.target.value })} data-testid="admin-lessons-heading" />
                    </div>
                    <div>
                      <Label>Introduction Text</Label>
                      <Textarea value={lessonsForm.intro} onChange={(e) => setLessonsForm({ ...lessonsForm, intro: e.target.value })} rows={3} data-testid="admin-lessons-intro" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label>Lesson Cards</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addLessonCard} className="text-[#4B1D5E] border-[#4B1D5E]" data-testid="admin-lessons-add-card"><Plus className="h-3 w-3 mr-1" /> Add Card</Button>
                      </div>
                      <div className="space-y-4">
                        {lessonsForm.cards.map((card, i) => (
                          <div key={i} className="p-4 bg-[#FAFAFA] rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-[#8A7E90]">Card {i + 1}</Label>
                              {lessonsForm.cards.length > 1 && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeLessonCard(i)} className="text-red-500 text-xs h-6"><Trash2 className="h-3 w-3 mr-1" /> Remove</Button>
                              )}
                            </div>
                            <Input placeholder="Card title" value={card.title} onChange={(e) => updateLessonCard(i, 'title', e.target.value)} className="h-9 text-sm" />
                            <Textarea placeholder="Description" value={card.desc} onChange={(e) => updateLessonCard(i, 'desc', e.target.value)} rows={2} className="text-sm" />
                            <Input placeholder="Tags (comma separated)" value={card.tags} onChange={(e) => updateLessonCard(i, 'tags', e.target.value)} className="h-9 text-sm" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg px-8" data-testid="admin-lessons-save">Save Lessons Page</Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36] mb-6">Site Settings</h2>
                <form onSubmit={saveSettings} className="space-y-6 max-w-xl">
                  <div className="space-y-4">
                    <h3 className="font-heading font-semibold text-[#2D1B36]">Taster Session</h3>
                    <div className="flex items-center gap-3">
                      <Switch checked={settingsForm.tasterEnabled} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, tasterEnabled: v })} data-testid="admin-settings-taster-toggle" />
                      <Label>Enable Taster Session CTA</Label>
                    </div>
                    <div><Label>Date</Label><Input value={settingsForm.tasterDate} onChange={(e) => setSettingsForm({ ...settingsForm, tasterDate: e.target.value })} placeholder="e.g. Saturday, 15th March 2026" data-testid="admin-settings-taster-date" /></div>
                    <div><Label>Time</Label><Input value={settingsForm.tasterTime} onChange={(e) => setSettingsForm({ ...settingsForm, tasterTime: e.target.value })} placeholder="e.g. 10:00 AM" data-testid="admin-settings-taster-time" /></div>
                    <div><Label>Location</Label><Input value={settingsForm.tasterLocation} onChange={(e) => setSettingsForm({ ...settingsForm, tasterLocation: e.target.value })} data-testid="admin-settings-taster-location" /></div>
                  </div>
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold text-[#2D1B36]">Pricing</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div><Label>Price (&pound;)</Label><Input value={settingsForm.lessonPrice} onChange={(e) => setSettingsForm({ ...settingsForm, lessonPrice: e.target.value })} data-testid="admin-settings-price" /></div>
                      <div><Label>Lessons</Label><Input value={settingsForm.lessonCount} onChange={(e) => setSettingsForm({ ...settingsForm, lessonCount: e.target.value })} data-testid="admin-settings-count" /></div>
                      <div><Label>Duration (min)</Label><Input value={settingsForm.lessonDuration} onChange={(e) => setSettingsForm({ ...settingsForm, lessonDuration: e.target.value })} data-testid="admin-settings-duration" /></div>
                    </div>
                  </div>
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold text-[#2D1B36]">Booking</h3>
                    <div className="flex items-center gap-3">
                      <Switch checked={settingsForm.waitlistMode} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, waitlistMode: v })} data-testid="admin-settings-waitlist-toggle" />
                      <Label>Switch "Book Lessons" to "Join Waiting List"</Label>
                    </div>
                  </div>
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold text-[#2D1B36]">Contact</h3>
                    <div><Label>Admin Email</Label><Input value={settingsForm.adminEmail} onChange={(e) => setSettingsForm({ ...settingsForm, adminEmail: e.target.value })} data-testid="admin-settings-email" /></div>
                  </div>
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold text-[#2D1B36] flex items-center gap-2"><Facebook className="h-4 w-4" /> Social Links</h3>
                    <p className="text-xs text-[#8A7E90]">Add your social media URLs to display icons in the footer.</p>
                    <div><Label>Facebook URL</Label><Input value={settingsForm.socialLinks?.facebook || ''} onChange={(e) => setSettingsForm({ ...settingsForm, socialLinks: { ...settingsForm.socialLinks, facebook: e.target.value } })} placeholder="https://facebook.com/singvolve" data-testid="admin-settings-facebook" /></div>
                    <div><Label>Instagram URL</Label><Input value={settingsForm.socialLinks?.instagram || ''} onChange={(e) => setSettingsForm({ ...settingsForm, socialLinks: { ...settingsForm.socialLinks, instagram: e.target.value } })} placeholder="https://instagram.com/singvolve" data-testid="admin-settings-instagram" /></div>
                    <div><Label>YouTube URL</Label><Input value={settingsForm.socialLinks?.youtube || ''} onChange={(e) => setSettingsForm({ ...settingsForm, socialLinks: { ...settingsForm.socialLinks, youtube: e.target.value } })} placeholder="https://youtube.com/@singvolve" data-testid="admin-settings-youtube" /></div>
                  </div>
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold text-[#2D1B36] flex items-center gap-2"><Image className="h-4 w-4" /> Site Photos</h3>
                    <p className="text-xs text-[#8A7E90]">Upload photos for the site. Images are compressed automatically. Leave unchanged to keep defaults.</p>
                    {[
                      { key: 'hero', label: 'Hero Image (Homepage)' },
                      { key: 'about', label: 'About Page Image' },
                      { key: 'location', label: 'Location Page Image' },
                    ].map(({ key, label }) => (
                      <div key={key} className="bg-[#FAFAFA] rounded-xl p-4">
                        <Label className="mb-2 block">{label}</Label>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm font-medium text-[#4B1D5E]" data-testid={`admin-upload-${key}`}>
                            <Upload className="h-4 w-4" />
                            {uploading[key] ? 'Compressing...' : 'Upload Photo'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploading(prev => ({ ...prev, [key]: true }));
                                try {
                                  const compressed = await compressImage(file);
                                  setSettingsForm(prev => ({
                                    ...prev,
                                    images: { ...prev.images, [key]: compressed }
                                  }));
                                } catch (err) {
                                  console.error('Compression error:', err);
                                }
                                setUploading(prev => ({ ...prev, [key]: false }));
                                e.target.value = '';
                              }}
                              disabled={uploading[key]}
                            />
                          </label>
                          {settingsForm.images?.[key] && (
                            <Button type="button" variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => setSettingsForm(prev => ({ ...prev, images: { ...prev.images, [key]: '' } }))}>
                              Remove
                            </Button>
                          )}
                        </div>
                        {settingsForm.images?.[key] && (
                          <img src={settingsForm.images[key]} alt={`${label} preview`} className="mt-3 h-32 rounded-lg object-cover border border-gray-200" />
                        )}
                      </div>
                    ))}
                  </div>
                  <Button type="submit" disabled={loading} className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg px-8" data-testid="admin-settings-save-btn">
                    {loading ? 'Saving...' : 'Save Settings'}
                  </Button>
                  {saveStatus && (
                    <p className={`text-sm mt-2 font-medium ${saveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {saveStatus.message}
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
